import * as React from "react";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";
import InputFactory from "./factories/inputFactory";
import { InputTypeEnum } from "../enums/inputTypeEnum";
import { InputMetaDataFactoryProps } from "../../interfaces/props/InputMetaDataFactoryProps";
import { dataSourceSharePointClassifier } from "../helpers/dataSourceSharePointClassifier";
import { SharePointInputData } from "../../interfaces/props/SharePointInputData";
import { inputResult } from "../helpers/inputResult";

const InputMetaDataFactory: React.FunctionComponent<InputMetaDataFactoryProps> = ({
  xml,
  choices,
  webUrl,
  sharepointService,
  inputData,
  handleInputChange,
  internalName,
  sharepointListId,
  regionalSetting,
  datasourceFields
}: InputMetaDataFactoryProps) => {
  const [error, setError] = React.useState("");
  const classifier = React.useRef(new dataSourceSharePointClassifier());
 
  React.useEffect(() => {
    const inputType: InputTypeEnum = classifier.current.getSharepointInputType(xml, datasourceFields);
    if (inputType == InputTypeEnum.Percentage && inputData?.value) {
      handleInputChange(inputData.index, inputData.value * 100, false);
    }

    if (
      (inputType == InputTypeEnum.DateOnly || inputType == InputTypeEnum.DateTime) &&
      (inputData?.value === "[today]")
    ) {
      handleInputChange(inputData.index, new Date(), false);
    }
  }, []);

  const isRequired = React.useCallback(() => {
    return xml.Required === SharePointMetadataBooleans.true;
  }, [xml]);

  const updateValue = React.useCallback((index: number, value: any, error: string) => {
    setError(error ? error : "");
    handleInputChange(index, value, error ? true : false);
  }, [setError, handleInputChange]);

  const updateInputValidationStatus = React.useCallback((result: inputResult): void => {   
    updateValue(inputData.index, result.value, result.error);
  }, [inputData, updateValue]);

  const determineInitialInputValue = React.useCallback((inputType: InputTypeEnum): SharePointInputData => {
    if (inputData) {
      if (inputType === InputTypeEnum.DateOnly || inputType === InputTypeEnum.DateTime) {
        if (typeof inputData.value === 'string') {
          return { ...inputData, value: inputData.value === "[today]" ? new Date() : inputData.value ? new Date(inputData.value) : null }
        }
      }

      if (inputType === InputTypeEnum.taxonomyDouble && inputData.value?.length > 0) {
        if (typeof inputData.value[0] === 'object') {
          return { ...inputData, value: inputData.value.map(x => x.Label) }
        }
      }
    }

    return inputData;
  }, [inputData]);

  const generateField = () => {
    let inputType = classifier.current.getSharepointInputType(xml, datasourceFields);
    const inputValue = determineInitialInputValue(inputType);
    return (
      <InputFactory
        sharepointService={sharepointService}
        UserSelectionScope={xml.UserSelectionScope}
        webUrl={webUrl}
        Choices={choices}
        displayName={xml.DisplayName}
        isRequired={isRequired}
        inputValue={inputValue}
        min={xml.Min}
        max={xml.Max}
        type={inputType}
        error={inputValue?.hasError ? (error || inputValue.errorMessage) : ""}
        validationProperties={{
          isRequired: isRequired(),
          min: xml.Min && Number.parseFloat(xml.Min),
          max: xml.Max && Number.parseFloat(xml.Max),
          decimals: xml.Decimals
        }}
        updateInputValidationStatus={updateInputValidationStatus}
        internalName={internalName}
        regionalSetting={regionalSetting}
        sharepointListId={sharepointListId}
        listId={xml?.List?.replace(/{|}/g, '')}
        columnInternalName={xml?.ShowField}
      />
    );
  };

  return generateField();
};

export default InputMetaDataFactory;